import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {concatLatestFrom} from '@ngrx/operators';
import {Store} from '@ngrx/store';
import {catchError, map, of, switchMap} from 'rxjs';
import {unrecoverableError} from '../common/error-handling/error-handling.actions';
import {CommerceAddressService} from '../data/api/service';
import * as addressActions from './address.actions';
import {selectSelectedAddress} from './address.selectors';
import {selectContextData} from '../common/context-data/context-data.selectors';
import {completeOrder} from '../common/shopping-cart/cart.actions';

@Injectable()
export class AddressEffects {
  saveValidatedAddress$ = createEffect(
    () => {
      return this.actions$.pipe(ofType(addressActions.saveValidatedAddress));
    },
    {dispatch: false},
  );

  createAddress$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.createAddress),
      concatLatestFrom(() => [
        this.store.select(selectSelectedAddress),
        this.store.select(selectContextData),
      ]),
      switchMap(([__action, address, contextData]) => {
        const shippingAddress = {
          ...(address.firstName === undefined
            ? {}
            : {firstName: address.firstName}),
          ...(address.lastName === undefined
            ? {}
            : {lastName: address.lastName}),
          address1: address.address1,
          address2: address.address2,
          city: address.city,
          state: address.state,
          zip: address.zip,
          country: address.country,
          billing: false,
          shipping: true,
        };

        return this.commerceAddressService
          .attachAddressToCart(String(contextData?.guestKey), shippingAddress)
          .pipe(
            // No intermediate "create" or "submit" actions needed:
            map(() => addressActions.submitAddressSuccess()),
            catchError(error =>
              of(
                addressActions.createAddressFailure({
                  error: {message: 'Unable to submit address', error},
                }),
              ),
            ),
          );
      }),
    );
  });

  createAddressFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.createAddressFailure),
      map(({error}) => unrecoverableError({error})),
    );
  });

  submitAddressSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.submitAddressSuccess),
      map(() => completeOrder()),
    );
  });

  constructor(
    private actions$: Actions,
    private commerceAddressService: CommerceAddressService,
    private store: Store,
  ) {}
}
